import type { FilterType } from '@studyportals/omnisearch-interfaces';
import type { SearchType } from '@domain/enums/SearchType';
import type { IRouteMeta } from '@app/interfaces/dtos/IRouteMeta';
import type { SearchRouteTable, SearchRouteTemplate } from './SearchRouteTable';
import type { ParamPatternRegistry, SegmentTest } from './ParamPatternRegistry';

export interface ResolvedSearchRoute {
	readonly name: string;
	readonly searchType: SearchType;
	readonly filterTypes: readonly FilterType[];
	readonly params: Readonly<Record<string, string>>;
}

export interface SearchRouteCandidate {
	readonly name: string;
	/** Full path template including the route prefix, e.g. `/search/:lv/:di` */
	readonly path: string;
	readonly meta: Readonly<Required<IRouteMeta>>;
}

type CompiledSegment =
	| { param: false, value: string }
	| { param: true, key: string, test: SegmentTest };

interface CompiledTemplate {
	candidate: SearchRouteCandidate;
	segments: CompiledSegment[];
}

type ParamValues = Partial<Record<string, string | string[] | null | undefined>>;

// Bounded so long-lived server processes can't grow it without limit.
const MAX_CACHED_PATHS = 1_000;

// Same encoding vue-router applies to params when building a path from a named route.
const encodeParam = (text: string): string => encodeURI(text)
	.replace(/%7C/g, '|')
	.replace(/%5B/g, '[')
	.replace(/%5D/g, ']')
	.replace(/#/g, '%23')
	.replace(/\?/g, '%3F')
	.replace(/\//g, '%2F');

// Same decoding vue-router applies to matched params.
const decodeParam = (text: string): string => {
	try {
		return decodeURIComponent(text);
	} catch {
		return text;
	}
};

const toSegments = (path: string): string[] => path.split('/');

/**
 * Matches paths of one search type against its generated route templates without registering those
 * templates in vue-router. Templates are compiled lazily on the first match and kept for the lifetime
 * of the process, so the per-request router only has to deal with a single catch-all route.
 *
 * Matching mirrors vue-router's defaults (non-strict trailing slash, case-insensitive, first match in
 * ranking order wins), so it resolves exactly the route vue-router used to resolve.
 */
export class SearchRouteResolver {
	public readonly searchType: SearchType;
	private readonly table: SearchRouteTable;
	private readonly patterns: ParamPatternRegistry;
	private readonly prefix: string;
	private rankedCandidates?: readonly SearchRouteCandidate[];
	private templatesBySegmentCount?: Map<number, CompiledTemplate[]>;
	private readonly cache = new Map<string, ResolvedSearchRoute | null>();

	constructor(table: SearchRouteTable, patterns: ParamPatternRegistry, prefix: string = '') {
		this.searchType = table.searchType;
		this.table = table;
		this.patterns = patterns;
		this.prefix = prefix;
	}

	/** All routes of this search type in ranking order. No pattern compilation needed. */
	public get candidates(): readonly SearchRouteCandidate[] {
		if (!this.rankedCandidates) {
			const { templates, matchOrder } = this.table;
			const order = matchOrder ?? templates.map((_, index) => index);

			this.rankedCandidates = Object.freeze(order.map(index => this.toCandidate(templates[index]!)));
		}

		return this.rankedCandidates;
	}

	public findCandidate(name: string): SearchRouteCandidate | undefined {
		return this.candidates.find(candidate => candidate.name === name);
	}

	public match(path: string): ResolvedSearchRoute | null {
		const cached = this.cache.get(path);
		if (cached !== undefined) {
			// Refresh recency so frequently used paths stay cached.
			this.cache.delete(path);
			this.cache.set(path, cached);
			return cached;
		}

		const resolved = this.resolve(path);
		if (this.cache.size >= MAX_CACHED_PATHS) {
			this.cache.delete(this.cache.keys().next().value!);
		}
		this.cache.set(path, resolved);

		return resolved;
	}

	/**
	 * Builds the path for a candidate. Like vue-router's named-route resolution, params missing from
	 * `params` are taken from `fallbackParams` (the current route).
	 */
	public build(candidate: SearchRouteCandidate, params: ParamValues, fallbackParams: ParamValues = {}): string {
		return toSegments(candidate.path)
			.map((segment) => {
				if (!segment.startsWith(':')) return segment;

				const key = segment.slice(1);
				const value = params[key] ?? fallbackParams[key];
				if (typeof value !== 'string' || value === '') {
					throw new Error(`Missing required param "${key}"`);
				}

				return encodeParam(value);
			})
			.join('/');
	}

	private toCandidate(template: SearchRouteTemplate): SearchRouteCandidate {
		return Object.freeze({
			name: template.name,
			path: `${this.prefix}${template.path}`,
			meta: Object.freeze({
				filterTypes: template.meta.filterTypes,
				searchType: this.searchType
			})
		});
	}

	private compiledTemplates(): Map<number, CompiledTemplate[]> {
		if (!this.templatesBySegmentCount) {
			const bySegmentCount = new Map<number, CompiledTemplate[]>();

			for (const candidate of this.candidates) {
				const segments = toSegments(candidate.path).map((segment): CompiledSegment => segment.startsWith(':')
					? { param: true, key: segment.slice(1), test: this.patterns.get(segment.slice(1)) }
					: { param: false, value: segment.toLowerCase() });

				const group = bySegmentCount.get(segments.length) ?? [];
				group.push({ candidate, segments });
				bySegmentCount.set(segments.length, group);
			}

			this.templatesBySegmentCount = bySegmentCount;
		}

		return this.templatesBySegmentCount;
	}

	private resolve(path: string): ResolvedSearchRoute | null {
		// Non-strict mode: a single trailing slash is optional.
		const normalisedPath = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
		const segments = toSegments(normalisedPath);
		const templates = this.compiledTemplates().get(segments.length);
		if (!templates) return null;

		for (const template of templates) {
			const params = this.matchSegments(template.segments, segments);
			if (!params) continue;

			return Object.freeze({
				name: template.candidate.name,
				searchType: this.searchType,
				filterTypes: template.candidate.meta.filterTypes,
				params: Object.freeze(params)
			});
		}

		return null;
	}

	private matchSegments(templateSegments: CompiledSegment[], segments: string[]): Record<string, string> | null {
		const params: Record<string, string> = {};

		for (let i = 0; i < templateSegments.length; i++) {
			const templateSegment = templateSegments[i]!;
			const segment = segments[i]!;

			if (!templateSegment.param) {
				if (segment.toLowerCase() !== templateSegment.value) return null;
				continue;
			}

			if (!templateSegment.test(segment)) return null;
			params[templateSegment.key] = decodeParam(segment);
		}

		return params;
	}
}
